import type { ICreateProductionSiteCmd } from "@/data/commands/catalog/CreateProductionSiteCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError, IDataSource } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import {
    JoinPrintingItemsMode,
    PrintFontSize,
    PrintLayout,
    PrintWhenOrderHasStatus,
    PrinterProtocolType,
} from "@/data/model/Enums";
import type { IProductionSite } from "@/data/model/ProductionSite";
import type { IRoomWithService } from "@/data/model/Room";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import { exceptionTypes } from "@/data/util/Config";
import { Misc } from "@/data/util/Misc";
import { Validation } from "@/data/util/Validation";

import { logError } from "@/shared/logging/errors";

import { i18n } from "@/portal/Trans";
import { PrintWhenOrderHasStatusesSelect } from "@/portal/elements/PrintWhenOrderHasStatusesSelect";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { UiNotification } from "@/portal/ui/Notification";

export class ProductionSiteForm extends BaseEntityForm<IProductionSite, ICreateProductionSiteCmd> {
    private $nameField: JQuery;
    private $notifyOnFailEmailField: JQuery;
    private isActiveCheck: Switch;
    private protocolSelect: Select<PrinterProtocolType>;
    private layoutSelect: Select<PrintLayout>;
    private printFontSizeSelect: Select<PrintFontSize>;
    private printWhenOrderHasStatusesSelect: PrintWhenOrderHasStatusesSelect;
    private backupSiteSelect: Select<IProductionSite>;
    private printFromRoomsSelect: Select<IRoomWithService>;
    private joinPrintingItemsModeSelect: Select<JoinPrintingItemsMode>;
    private printFromAllRoomsSwitch: Switch;
    private orderPrintsWithoutHeaderSwitch: Switch;
    private cashDrawerConnectedSwitch: Switch;

    private $alwaysPrintOutBillForOperatorPayments: JQuery;
    private alwaysPrintOutBillForOperatorPaymentsSwitch: Switch;
    private $printFromRoomsForm: JQuery;

    private didShowWarning = false;
    private initialLayout?: PrintLayout;
    private initialProtocol?: PrinterProtocolType;
    private isReadOnly = false;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).productionSiteDataSource);

        this.$nameField = this.$form
            .find('.form-control[name="name"]')
            .change(() => this.updateName(this.$nameField.val()));
        this.$notifyOnFailEmailField = this.$form.find('.form-control[name="notifyonfailemail"]');
        this.$printFromRoomsForm = this.$form.find("#print-from-room-form");

        const protocolSelect = this.$form.find('.form-control[name="protocol"]');
        Misc.eachEnum(PrinterProtocolType, (i, e) => {
            protocolSelect.append('<option value="' + i + '">' + i18n("enums.PrinterProtocolType." + e) + "</option>");
        });

        this.protocolSelect = Select.createFromSelect(protocolSelect);
        this.protocolSelect.onChange = () => {
            this.showWarning();
        };

        const layoutSelect = this.$form.find('.form-control[name="layout"]');
        Misc.eachEnum(PrintLayout, (i, e) => {
            layoutSelect.append('<option value="' + i + '">' + i18n("enums.PrintLayout." + e) + "</option>");
        });

        this.layoutSelect = Select.createFromSelect(layoutSelect);
        this.layoutSelect.onChange = () => {
            this.showWarning();
        };

        const printFontSizeSelect = this.$form.find('.form-control[name="print-font-size"]');
        Misc.eachEnum(PrintFontSize, (i, e) => {
            printFontSizeSelect.append('<option value="' + i + '">' + i18n("enums.PrintFontSize." + e) + "</option>");
        });
        this.printFontSizeSelect = Select.createFromSelect(printFontSizeSelect);

        this.printWhenOrderHasStatusesSelect = new PrintWhenOrderHasStatusesSelect(
            this.$form.find('.form-control[name="print-when-order-has-statuses"]')
        );

        this.printWhenOrderHasStatusesSelect.setAllowedOptions(
            PrintWhenOrderHasStatus.None | PrintWhenOrderHasStatus.Confirmed | PrintWhenOrderHasStatus.Ready
        );

        this.isActiveCheck = new Switch(this.$form.find('input[name="isactive"]'));
        this.printFromAllRoomsSwitch = new Switch(this.$form.find('input[name="printReceiptsFromAllRoomsInput"]'));
        this.printFromAllRoomsSwitch.onChange = () => {
            this.setPrintFromRoomsSectionVisibility();
            this.setAlwaysPrintOutBillForOperatorPaymentsVisibility();
        };

        this.orderPrintsWithoutHeaderSwitch = new Switch(
            this.$form.find('input[name="orderPrintsWithoutHeaderInput"]')
        );

        this.cashDrawerConnectedSwitch = new Switch(this.$form.find('input[name="cashDrawerConnectedInput"]'));

        this.$alwaysPrintOutBillForOperatorPayments = this.$form.find(
            "#always-print-out-bill-for-operator-payments-form"
        );
        this.alwaysPrintOutBillForOperatorPaymentsSwitch = new Switch(
            this.$form.find('input[name="alwaysPrintOutBillForOperatorPaymentsInput"]')
        );

        const joinPrintingItemsModeSelect = this.$form.find('.form-control[name="joinprintingitems"]');
        this.joinPrintingItemsModeSelect = Select.createFromSelect(joinPrintingItemsModeSelect);
        Misc.eachEnum(JoinPrintingItemsMode, (i, e) => {
            joinPrintingItemsModeSelect.append(
                '<option value="' + i + '">' + i18n("enums.JoinPrintingItemsMode." + e) + "</option>"
            );
        });

        const backupSiteDs: IDataSource<IProductionSite> = {
            getData: (filter: IPaginatedQry, abortSignal?: AbortSignal) => {
                return new Promise((resolve, reject) => {
                    return this.dataSource
                        .getData(filter, abortSignal)
                        .then((s) => {
                            if (this.id !== undefined) {
                                s.items = s.items.filter((c) => c.id != this.id);
                                s.hasNext = false;
                                s.hasPrev = false;
                            }

                            resolve(s);
                        })
                        .catch((ex) => {
                            logError(ex);
                            reject(ex);
                        });
                });
            },
        };

        this.backupSiteSelect = new Select<IProductionSite>(
            this.$form.find('select[name="backupsite"]'),
            backupSiteDs,
            (c) => c.name,
            false,
            i18n("productionSite.selectProductionSite")
        );
        this.printFromRoomsSelect = Select.createFromSelect(
            this.$form.find('.form-control[name="printfromroom"]'),
            Factory.getInstance<RoomDao>(RoomDao),
            (c) => c.name,
            false,
            i18n("general.none"),
            undefined,
            true
        );

        this.printFromRoomsSelect.onChange = () => {
            this.setAlwaysPrintOutBillForOperatorPaymentsVisibility();
        };
    }

    public setFormData(model?: IProductionSite) {
        const isReadOnly = this.isReadOnly;

        if (isReadOnly) this.setReadOnly(false);

        this.updateName(model === undefined ? "" : model.name);
        this.$nameField.val(model === undefined ? "" : model.name);
        this.$notifyOnFailEmailField.val(
            model === undefined || model.notifyOnFailEmail === undefined ? "" : model.notifyOnFailEmail
        );
        this.printWhenOrderHasStatusesSelect.setValues(model?.printWhenOrderHasStatuses ?? PrintWhenOrderHasStatus.Confirmed);

        this.orderPrintsWithoutHeaderSwitch.setValue(model?.orderPrintsWithoutHeader ?? false);
        this.isActiveCheck.setValue(model === undefined ? true : model.isActive);
        this.initialProtocol = model === undefined ? PrinterProtocolType.EscPos : model.printerProtocolType;
        this.protocolSelect.setValue(this.initialProtocol);
        this.backupSiteSelect.setValue(model === undefined ? undefined : model.backupProductionSiteId);
        this.initialLayout = model === undefined ? PrintLayout.OrderAndPay80Mm : model.printLayout;
        this.layoutSelect.setValue(this.initialLayout);
        this.printFontSizeSelect.setValue(model?.printFontSize ?? PrintFontSize.Small);
        this.printFromAllRoomsSwitch.setValue(model === undefined ? false : model.printReceiptsFromAllRooms);
        this.printFromRoomsSelect.setValues(
            model == null ? undefined : (model.printFromRooms.map((room) => room.id) as any)
        );
        this.alwaysPrintOutBillForOperatorPaymentsSwitch.setValue(
            model === undefined ? false : model.alwaysPrintOutBillForOperatorPayments
        );
        this.joinPrintingItemsModeSelect.setValue(
            model?.joinPrintingItemsMode == null ? JoinPrintingItemsMode.PerOrder : model.joinPrintingItemsMode
        );

        this.cashDrawerConnectedSwitch.setValue(model?.cashDrawerConnected ?? false);

        this.setReadOnly(isReadOnly);
        this.setPrintFromRoomsSectionVisibility();
        this.setAlwaysPrintOutBillForOperatorPaymentsVisibility();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateProductionSiteCmd {
        const data: ICreateProductionSiteCmd = {
            isActive: this.isActiveCheck.getValue(),
            name: this.$nameField.val().length === 0 ? undefined : this.$nameField.val(),
            printerProtocolType: parseInt(this.protocolSelect.getValue()!),
            printLayout: parseInt(this.layoutSelect.getValue()!),
            printFontSize: parseInt(this.printFontSizeSelect.getValue()!),
            notifyOnFailEmail:
                this.$notifyOnFailEmailField.val().length === 0 ? undefined : this.$notifyOnFailEmailField.val(),
            backupProductionSiteId: this.backupSiteSelect.getValue(),
            printReceiptFromRoomIds: this.printFromRoomsSelect.getValues(),
            joinPrintingItemsMode: parseInt(this.joinPrintingItemsModeSelect.getValue()!),
            printReceiptsFromAllRooms: this.printFromAllRoomsSwitch.getValue(),
            alwaysPrintOutBillForOperatorPayments: this.alwaysPrintOutBillForOperatorPaymentsSwitch.getValue(),
            orderPrintsWithoutHeader: this.orderPrintsWithoutHeaderSwitch.getValue(),
            cashDrawerConnected: this.cashDrawerConnectedSwitch.getValue(),
            printWhenOrderHasStatuses: this.printWhenOrderHasStatusesSelect.getValue(),
        };

        let hasError = false;

        if (data.name === undefined || data.name.length > 128) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        }

        if (data.notifyOnFailEmail !== undefined && !Validation.isMultiEmail(data.notifyOnFailEmail)) {
            hasError = true;
            this.$notifyOnFailEmailField.addClass("is-invalid");
        }

        if (isNaN(data.printLayout)) {
            hasError = true;
            this.layoutSelect.setError(true);
        }

        if (isNaN(data.joinPrintingItemsMode)) {
            hasError = true;
            this.joinPrintingItemsModeSelect.setError(true);
        }

        if (isNaN(data.printerProtocolType)) {
            hasError = true;
            this.protocolSelect.setError(true);
        }

        return (hasError ? undefined : data) as any;
    }

    public setReadOnly(isReadonly: boolean) {
        this.isReadOnly = isReadonly;

        this.hideWriteActions(isReadonly);
        this.$nameField.prop("readonly", isReadonly);
        this.$notifyOnFailEmailField.prop("readonly", isReadonly);
        this.isActiveCheck.setDisabled(isReadonly);
        this.protocolSelect.setDisabled(isReadonly);
        this.backupSiteSelect.setDisabled(isReadonly);
        this.layoutSelect.setDisabled(isReadonly);
        this.joinPrintingItemsModeSelect.setDisabled(isReadonly);
        this.printFontSizeSelect.setDisabled(isReadonly);
        this.printFromRoomsSelect.setDisabled(isReadonly);
        this.printFromAllRoomsSwitch.setDisabled(isReadonly);
        this.orderPrintsWithoutHeaderSwitch.setDisabled(isReadonly);
        this.alwaysPrintOutBillForOperatorPaymentsSwitch.setDisabled(isReadonly);
        this.cashDrawerConnectedSwitch.setDisabled(isReadonly);

        if (isReadonly) {
            this.$nameField.addClass("disabled");
            this.$notifyOnFailEmailField.addClass("disabled");
        } else {
            this.$nameField.removeClass("disabled");
            this.$notifyOnFailEmailField.removeClass("disabled");
        }
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.productionSiteUsedAsBackupException:
                return i18n("productionSite.usedAsBackup", {
                    forwardingProductionSites: b.additionalData.forwardingProductionSites,
                });
            default:
                return undefined;
        }
    }

    private showWarning() {
        if (this.didShowWarning || this.id === undefined) return;

        UiNotification.confirm(
            i18n("productionSite.changeProtocolHint"),
            i18n("general.attention"),
            () => {
                this.didShowWarning = true;
            },
            i18n("general.change"),
            () => {
                this.layoutSelect.setValue(this.initialLayout);
                this.protocolSelect.setValue(this.initialProtocol);
            },
            undefined,
            undefined,
            undefined,
            undefined,
            () => {
                this.layoutSelect.setValue(this.initialLayout);
                this.protocolSelect.setValue(this.initialProtocol);
            }
        );
    }

    private setAlwaysPrintOutBillForOperatorPaymentsVisibility() {
        const printFromAllRooms = this.printFromAllRoomsSwitch.getValue();
        const selectedRooms = this.printFromRoomsSelect.getValues();
        if (selectedRooms.length > 0 || printFromAllRooms) {
            this.$alwaysPrintOutBillForOperatorPayments.show();
        } else {
            this.$alwaysPrintOutBillForOperatorPayments.hide();
        }
    }

    private setPrintFromRoomsSectionVisibility() {
        const printFromAllRooms = this.printFromAllRoomsSwitch.getValue();
        if (printFromAllRooms) {
            this.$printFromRoomsForm.hide();
        } else {
            this.$printFromRoomsForm.show();
        }
    }
}
